import { MemberMapResults, ProviderMapResults, ProviderResultsSummaryMap } from "@app/projects/results/common/mapping";
import React, { createContext, FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  NetworkAssessmentAnalysisType,
  ProviderActionStatusEnum,
  RegionSetStatusType
} from "@app/projects/enumerators";
import { GainsPercentEnum } from "@app/projects/servicing-provider/types";
import { FeatureCollection, Geometry, Point } from "geojson";
import MemberSetService from "@app/members/memberSetService";
import GeographyService from "@app/geography/geographyService";
import { bbox } from "@turf/bbox";
import type { MapRef } from "react-map-gl/mapbox";
import * as ReactMapGL from "react-map-gl/mapbox";
import { LngLatBounds } from "mapbox-gl"
import { MappingContext } from "@app/mapping/MappingContext";
import { FilterContext } from "@app/projects/network-assessment/maps/network-coverage-map/contexts/FilterContext";
import {
  ProviderAccordionSelectedTab,
  ProviderFetchMessageStatus,
  ProvidersFilterOptions,
  ProvidersSortOptions,
  InsufficientClaimsVolumeLabels
} from "@app/projects/network-assessment/maps/network-coverage-map/enumerators";
import { featureCollection, point } from "@turf/helpers";
import {
  CountyContextModel,
  ProviderFetchMessageData,
  ProvidersFilterOptionsType,
  ZipCodeModel
} from "@app/projects/network-assessment/maps/network-coverage-map/interfaces";
import { ClaimsActivityLineOfBusinessTypeEnum, ClaimsActivityVolumeLabelEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import {
  HUNDRED_PERCENT_FILTER,
  PROVIDERS_LAYER_ID,
  ZERO_PERCENT_FILTER,
} from "@app/projects/network-assessment/maps/network-coverage-map/NetworkCoverageMapsConstants";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import ProjectService from "@app/projects/projectService";
import AdequacySummaryByRegionSet from "@app/projects/results/adequacy/adequacySummaryByRegionSet";
import AdequacySummarySpecialtyGroup from "@app/projects/results/adequacy/adequacySummarySpecialtyGroup";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user/UserContext";
import Features from "@app/feature-flags/features";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";

/* oxlint-disable sort-keys */
interface NetworkCoverageMapDataContextProps {
  applyMemberLayerDefaults: () => void;
  availablePageSizes: number[];
  countyBorderData: FeatureCollection | null;
  countyContextData: CountyContextModel | null;
  currentAdequacySummarySpecialtyGroup: AdequacySummarySpecialtyGroup;
  currentMapBoundary: ReactMapGL.LngLatBoundsLike | null;
  dataBoundingBox: ReactMapGL.LngLatBoundsLike | null;
  dataLoaded: boolean;
  filterGainsMinPercent: number;
  filterGainsMaxPercent: number;
  filterImpactsMinPercent: number;
  filterImpactsMaxPercent: number;
  filterServesMinPercent: number;
  filterServesMaxPercent: number;
  filterProvidersByNetworkAndActionType: (propviders: ProviderMapResults[]) => ProviderMapResults[];
  filterAvailableProviderSources: (providerFilterOptions: ProvidersFilterOptions[]) => ProvidersFilterOptions[];
  getSelectedSortValue: () => ProvidersSortOptions;
  handleProviderCardClick: (provider: ProviderMapResults) => void;
  handleProviderIconClick: (e: ReactMapGL.MapMouseEvent) => void;
  handleProviderPopupClose: () => void;
  hideNetworkProviderFilter: boolean,
  hideMarketProviderFilter: boolean,
  hideProspectProviderFilter: boolean,
  impactCardHeight: number;
  impactPageSize: number;
  impactProviderCount: number;
  impactProvidersPage: number;
  impactProviderFeatures?: FeatureCollection<Point, ProviderMapResults> | null;
  providersFilterOptions: ProvidersFilterOptionsType[];
  isProviderAccordionOpen: boolean,
  isProviderPopupOpen: boolean,
  loadingMembers: boolean;
  loadingProviders: boolean;
  mapRef: React.RefObject<MapRef>;
  mapStyle: string;
  memberFeatures?: FeatureCollection<Point, MemberMapResults> | null;
  members: MemberMapResults[] | null;
  minZoomLevel: number;
  networkCardHeight: number;
  networkPageSize: number;
  networkProviderCount: number;
  networkProvidersPage: number;
  pagedImpactProviders: ProviderMapResults[];
  pagedNetworkProviders: ProviderMapResults[];
  pcaIndicatorIncludesInsufficient: (value: string) => boolean;
  providerAccordionSelectedTab: ProviderAccordionSelectedTab;
  providerFeatures?: FeatureCollection<Point, ProviderMapResults> | null;
  providerFetchMessages: ProviderFetchMessageData[];
  providers: ProviderMapResults[] | null;
  providerSearchText: string;
  providerSourceFiltersComparer: (a: ProvidersFilterOptions, b: ProvidersFilterOptions) => number;
  resetMapData: () => void;
  selectedEffectiveness: number[],
  selectedEfficiency: number[],
  selectedLineOfBusiness: ClaimsActivityLineOfBusinessTypeEnum | null;
  selectedProviderFilterOptions: ProvidersFilterOptions[];
  selectedProvider: ProviderMapResults;
  selectedProviderFeature?: FeatureCollection<Point, ProviderMapResults> | null;
  selectedVolumeIndicators: DropdownItem<ClaimsActivityVolumeLabelEnum>[];
  totalImpactPages: number;
  totalNetworkPages: number;
  visibleAccordionProviders: ProviderMapResults[];
  setCurrentMapBoundary: React.Dispatch<React.SetStateAction<ReactMapGL.LngLatBoundsLike>>,
  setFilterGainsMinPercent: React.Dispatch<React.SetStateAction<number>>;
  setFilterGainsMaxPercent: React.Dispatch<React.SetStateAction<number>>;
  setFilterImpactsMinPercent: React.Dispatch<React.SetStateAction<number>>;
  setFilterImpactsMaxPercent: React.Dispatch<React.SetStateAction<number>>;
  setFilterServesMinPercent: React.Dispatch<React.SetStateAction<number>>;
  setFilterServesMaxPercent: React.Dispatch<React.SetStateAction<number>>;
  setImpactPageSize: React.Dispatch<React.SetStateAction<number>>;
  setImpactProvidersPage: React.Dispatch<React.SetStateAction<number>>;
  setIsProviderAccordionOpen: (isProviderAccordionOpen: boolean) => void;
  setMapStyle: React.Dispatch<React.SetStateAction<string>>;
  setMinZoomLevel: React.Dispatch<React.SetStateAction<number>>;
  setNetworkPageSize: React.Dispatch<React.SetStateAction<number>>;
  setNetworkProvidersPage: React.Dispatch<React.SetStateAction<number>>;
  setPagedImpactProviders: React.Dispatch<React.SetStateAction<ProviderMapResults[]>>;
  setPagedNetworkProviders: React.Dispatch<React.SetStateAction<ProviderMapResults[]>>;
  setProviderAccordionSelectedTab: React.Dispatch<React.SetStateAction<ProviderAccordionSelectedTab>>;
  setProviderSearchText: React.Dispatch<React.SetStateAction<string>>;
  setSelectedEffectiveness: React.Dispatch<React.SetStateAction<number[]>>;
  setSelectedEfficiency: React.Dispatch<React.SetStateAction<number[]>>;
  setSelectedLineOfBusiness: React.Dispatch<React.SetStateAction<ClaimsActivityLineOfBusinessTypeEnum | null>>;
  setSelectedProviderFilterOptions: React.Dispatch<React.SetStateAction<ProvidersFilterOptions[]>>;
  setSelectedSortValue: (selectSort: ProvidersSortOptions) => void;
  setSelectedVolumeIndicators: React.Dispatch<React.SetStateAction<DropdownItem<ClaimsActivityVolumeLabelEnum>[]>>;
  setShowMembersWithAccess: React.Dispatch<React.SetStateAction<boolean>>,
  setShowMembersWithoutAccess: React.Dispatch<React.SetStateAction<boolean>>,
  setShowExcludedProviders: React.Dispatch<React.SetStateAction<boolean>>,
  setTotalImpactPages: React.Dispatch<React.SetStateAction<number>>,
  setTotalNetworkPages: React.Dispatch<React.SetStateAction<number>>,
  setShowZipCodes: React.Dispatch<React.SetStateAction<boolean>>,
  setShowZipPoints: React.Dispatch<React.SetStateAction<boolean>>,
  showMembersWithAccess: boolean,
  showMembersWithoutAccess: boolean,
  showNetworkProvidersTab: boolean,
  showOutOfNetworkProvidersTab: boolean,
  showZipCodes: boolean,
  showZipPoints: boolean,
  showExcludedProviders: boolean,
  toggleProviderAccordion: () => void;
  triggerDataRefresh: () => void;
  zipCodeData: FeatureCollection<Geometry, ZipCodeModel>;
}
/* oxlint-enable sort-keys */

const nonTerminatedFilterOption = {
  id: 'provider-accordion-filter-non-terminated',
  name: 'Non-Terminated',
  type: ProvidersFilterOptions.NonTerminated,
}

const terminatedFilterOption = {
  id: 'provider-accordion-filter-terminated',
  name: 'Terminated',
  type: ProvidersFilterOptions.Terminated,
}

const availableMarketFilterOption = {
  id: 'provider-accordion-filter-market-available',
  name: 'Available',
  type: ProvidersFilterOptions.AvailableMarket,
}

const targetedMarketFilterOption = {
  id: 'provider-accordion-filter-market-targeted',
  name: 'Targeted',
  type: ProvidersFilterOptions.TargetedMarket,
}

const excludedMarketFilterOption = {
  id: 'provider-accordion-filter-excluded-market',
  name: 'Excluded',
  type: ProvidersFilterOptions.ExcludedMarket,
}

const availableProspectFilterOption = {
  id: 'provider-accordion-filter-prospect-available',
  name: 'Available',
  type: ProvidersFilterOptions.AvailableProspect,
}

const targetedProspectFilterOption = {
  id: 'provider-accordion-filter-prospect-targeted',
  name: 'Targeted',
  type: ProvidersFilterOptions.TargetedProspect,
}

const excludedProspectFilterOption = {
  id: 'provider-accordion-filter-excluded-prospect',
  name: 'Excluded',
  type: ProvidersFilterOptions.ExcludedProspect,
}

const baseProvidersFilterOptionsAdvancedFiltersTemplate: ProvidersFilterOptionsType[] = [
  {
    children: [],
    id: 'provider-accordion-filter-network',
    name: 'Network Providers',
    type: ProvidersFilterOptions.Network,
  },
  {
    children: [],
    id: 'provider-accordion-filter-market',
    name: 'Market Providers',
    type: ProvidersFilterOptions.Market,
  },
  {
    children: [],
    id: 'provider-accordion-filter-prospect',
    name: 'Prospect Providers',
    type: ProvidersFilterOptions.Prospect,
  },
];

const NetworkCoverageMapDataContext = createContext<Partial<NetworkCoverageMapDataContextProps>>({});

const NetworkCoverageMapDataContextProvider: FC<{ children: React.ReactNode }> = (props: { children: React.ReactNode }) => {
  const projectService = new ProjectService();

  const mapRef = useRef<MapRef>(null);

  // Contexts
  const projectContext = useContext(CountyProjectContext);
  const filterContext = useContext(FilterContext);
  const mappingContext = useContext(MappingContext);
  const userContext = useContext(UserContext);
  const featureFlags = useContext(FeatureFlagContext);

  const pciEnabled = filterContext.adequacyTimeslices.find(x => x.projectRunConfigurationId == filterContext.selectedTimeslice)?.showPciData;

  const serviceAreaWideMapping = featureFlags.enabledFeatureFlags.has(Features.SERVICE_AREA_WIDE_MAPPING_COUNTY);
  const isMultiCounty = serviceAreaWideMapping && filterContext.selectedServiceAreas.length > 1;
  const hasSelectedServiceArea = serviceAreaWideMapping
    ? filterContext.selectedServiceAreas.length > 0
    : !!filterContext.selectedServiceArea;

  const previouslyVisibleProviderFilterOptions = useRef<Set<ProvidersFilterOptions>>(new Set());

  const baseProvidersFilterOptionsSet = new Set(baseProvidersFilterOptionsAdvancedFiltersTemplate.map(opt => opt.type));

  const baseProvidersFilterOptions = baseProvidersFilterOptionsAdvancedFiltersTemplate;

  /* Data loading related values */
  const [dataLoaded, setDataLoaded] = useState<boolean>(false);
  const [loadingCountyBorders, setLoadingCountyBorders] = useState<boolean>(false);
  const [loadingMembers, setLoadingMembers] = useState<boolean>(false);
  const [loadingProviders, setLoadingProviders] = useState<boolean>(false);
  const [loadingZipCodes, setLoadingZipCodes] = useState<boolean>(false);
  const hasRequestedMembers = useRef<boolean>(false);
  const latestMemberRequestId = useRef<number>(0);
  const latestCountyContextRequestId = useRef<number>(0);
  const countyContextSelection = useRef<string | null>(null);
  const [dataRefreshCounter, setDataRefreshCounter] = useState<number>(0);

  /* Provider summary data */
  const [summaryMap, setSummaryMap] = useState<ProviderResultsSummaryMap>({} as ProviderResultsSummaryMap);

  // Predicates for filtering and sorting providers
  const getNetworkProviders = (providers: ProviderMapResults[]): ProviderMapResults[] => {
    return providers.filter(p => p.providerNetworkType === ProviderNetworkTypeEnum.Network) ?? [];
  }

  const getImpactProviders = (providers: ProviderMapResults[]): ProviderMapResults[] => {
      return providers.filter(p => p.providerNetworkType !== ProviderNetworkTypeEnum.Network) ?? [];
  }

  /* Providers data */
  const [providers, setProviders] = useState<ProviderMapResults[] | null>(null);
  const [visibleAccordionProviders, setVisibleAccordionProviders] = useState<ProviderMapResults[]>([]);
  const [visibleMapProviders, setVisibleMapProviders] = useState<ProviderMapResults[]>([]);

  const [providerFetchMessages, setProviderFetchMessages] = useState<ProviderFetchMessageData[]>([]);

  /* Provider search, filter and sort related values */
  const [providerSearchText, setProviderSearchText] = useState<string>('');
  const [providerAccordionSelectedTab, setProviderAccordionSelectedTab] = useState(ProviderAccordionSelectedTab.NetworkProviders);
  const [selectedProviderFilterOptions, setSelectedProviderFilterOptions] = useState<ProvidersFilterOptions[]>([]);
  const [providersFilterOptions, setProviderFilterOptions] = useState<ProvidersFilterOptionsType[]>(baseProvidersFilterOptions);
  const [selectedNetworkProviderSortOption, setSelectedNetworkProviderSortOption] = useState<ProvidersSortOptions>(ProvidersSortOptions.ServesHighToLow);
  const [selectedImpactProviderSortOption, setSelectedImpactProviderSortOption] = useState<ProvidersSortOptions>(ProvidersSortOptions.ImpactsHighToLow);
  const [selectedProvider, setSelectedProvider] = useState<ProviderMapResults>(null);
  const [showNetworkProvidersTab, setShowNetworkProvidersTab] = useState(true);
  const [showOutOfNetworkProvidersTab, setShowOutOfNetworkProvidersTab] = useState(false);
  const [hideNetworkProviderFilter, setHideNetworkProviderFilter] = useState(false);
  const [hideMarketProviderFilter, setHideMarketProviderFilter] = useState(false);
  const [hideProspectProviderFilter, setHideProspectProviderFilter] = useState(false);
  const [filterGainsMinPercent, setFilterGainsMinPercent] = useState<number>(ZERO_PERCENT_FILTER);
  const [filterGainsMaxPercent, setFilterGainsMaxPercent] = useState<number>(HUNDRED_PERCENT_FILTER);
  const [filterServesMinPercent, setFilterServesMinPercent] = useState<number>(ZERO_PERCENT_FILTER);
  const [filterServesMaxPercent, setFilterServesMaxPercent] = useState<number>(HUNDRED_PERCENT_FILTER);
  const [filterImpactsMinPercent, setFilterImpactsMinPercent] = useState<number>(ZERO_PERCENT_FILTER);
  const [filterImpactsMaxPercent, setFilterImpactsMaxPercent] = useState<number>(HUNDRED_PERCENT_FILTER);
  const [selectedLineOfBusiness, setSelectedLineOfBusiness] = useState<ClaimsActivityLineOfBusinessTypeEnum | null>(null);
  const [selectedVolumeIndicators, setSelectedVolumeIndicators] = useState<DropdownItem<ClaimsActivityVolumeLabelEnum>[]>([]);
  const [selectedEffectiveness, setSelectedEffectiveness] = useState<number[]>([]);
  const [selectedEfficiency, setSelectedEfficiency] = useState<number[]>([]);
  const [showExcludedProviders, setShowExcludedProviders] = useState<boolean>(false);

  /* Members data */
  const [members, setMembers] = useState<MemberMapResults[] | null>(null);
  const [showMembersWithAccess, setShowMembersWithAccess] = useState<boolean>(true);
  const [showMembersWithoutAccess, setShowMembersWithoutAccess] = useState<boolean>(true);

  /* Map related values */
  const [countyBorderData, setCountyBorderData] = useState<FeatureCollection | null>(null);
  const [countyContextData, setCountyContextData] = useState<CountyContextModel | null>(null);
  const [dataBoundingBox, setDataBoundingBox] = useState<ReactMapGL.LngLatBoundsLike | null>(null);
  const [currentMapBoundary, setCurrentMapBoundary] = useState<ReactMapGL.LngLatBoundsLike | null>(null);
  const [mapStyle, setMapStyle] = useState<string>(mappingContext.qesOutdoorsStyle);
  const [minZoomLevel, setMinZoomLevel] = useState<number>(3);
  const [zipCodeData, setZipCodeData] = useState<FeatureCollection<Geometry, ZipCodeModel>>(featureCollection([]));
  const [showZipCodes, setShowZipCodes] = useState<boolean>(false);
  const [showZipPoints, setShowZipPoints] = useState<boolean>(false);

  /* Provider Accordion values */
  const availablePageSizes = [25, 50, 100];
  const impactCardHeight = 139;
  const networkCardHeight = 116;

  // Pagination state
  const [impactPageSize, setImpactPageSize] = useState<number>(50);
  const [networkPageSize, setNetworkPageSize] = useState<number>(50);
  const [impactProvidersPage, setImpactProvidersPage] = useState<number>(1);
  const [networkProvidersPage, setNetworkProvidersPage] = useState<number>(1);
  const [pagedImpactProviders, setPagedImpactProviders] = useState<ProviderMapResults[]>([]);
  const [pagedNetworkProviders, setPagedNetworkProviders] = useState<ProviderMapResults[]>([]);
  const [totalImpactPages, setTotalImpactPages] = useState<number>(0);
  const [totalNetworkPages, setTotalNetworkPages] = useState<number>(0);

  /* Miscellaneous */
  const [currentAdequacySummarySpecialtyGroup, setCurrentAdequacySummarySpecialtyGroup] = useState<AdequacySummarySpecialtyGroup | null>(null);
  const [isProviderPopupOpen, setIsProviderPopupOpen] = useState(false);
  const [isProviderAccordionOpen, setIsProviderAccordionOpen] = useState(true);

  const memberSetService = new MemberSetService();
  const geographyService = new GeographyService();

  const getRegionDetails = async () => {
      const regionDetails: AdequacySummaryByRegionSet[] =
        await projectService.regionDetailsByRegionSet(
          projectContext.project.projectId,
          Number(filterContext.selectedServiceArea),
          projectContext.runConfiguration.projectRunConfigurationId,
          filterContext.selectedSpecialtyGroupCode.key,
          filterContext.getCurrentServiceAreaStatus() == RegionSetStatusType.Opportunity
            ? NetworkAssessmentAnalysisType.Opportunity
            : NetworkAssessmentAnalysisType.ServiceArea);

    const selectedSpecialtyGroupSummary = regionDetails[0].specialtyGroupSummaries[0];
    setCurrentAdequacySummarySpecialtyGroup(selectedSpecialtyGroupSummary);
  }

  const getZipCodeData = async () => {
    try {
      const zipCodeData: FeatureCollection<Geometry, ZipCodeModel> =
        serviceAreaWideMapping ?
          await geographyService.getZipCodeBordersByRegionSetIds(filterContext.selectedServiceAreas)
          : await geographyService.getZipCodeBordersByRegionSetId(filterContext.selectedServiceArea);

      setZipCodeData(zipCodeData);
    } catch (error) {
      setZipCodeData(featureCollection([]));
    } finally {
      setLoadingZipCodes(false);
    }
  }

  const toggleProviderAccordion = () => {
    setIsProviderAccordionOpen(!isProviderAccordionOpen);
  }

  const fetchMemberData = React.useCallback(async (): Promise<void> => {
    const requestId = ++latestMemberRequestId.current;

    if (!hasSelectedServiceArea ||
      !filterContext.selectedSpecialtyGroupCode ||
      !projectContext?.runConfiguration?.projectRunConfigurationId
    ) {
      setMembers(null);
      setLoadingMembers(false);
      return;
    }

    try {
      const fetchedMemberData = serviceAreaWideMapping ?
        await memberSetService.GetMemberResultsMultiRegionAsync(
          projectContext.project.projectId,
          projectContext?.runConfiguration?.projectRunConfigurationId ?? null,
          filterContext.selectedServiceAreas,
          filterContext.selectedSpecialtyGroupCode.key
        ) :
        await memberSetService.GetMemberResultsAsync(
          projectContext.project.projectId,
          projectContext?.runConfiguration?.projectRunConfigurationId ?? null,
          filterContext.selectedServiceArea,
          filterContext.selectedSpecialtyGroupCode.key
        );

      if (requestId !== latestMemberRequestId.current) return;

      setMembers(fetchedMemberData ?? []);
      setLoadingMembers(false);
    } catch (error) {
      if (requestId !== latestMemberRequestId.current) return;

      setMembers(null);
      setLoadingMembers(false);
    }
  }, [
    hasSelectedServiceArea,
    filterContext.selectedServiceArea,
    filterContext.selectedServiceAreas,
    filterContext.selectedSpecialtyGroupCode.key,
    memberSetService,
    projectContext.project.projectId,
    projectContext?.runConfiguration?.projectRunConfigurationId
  ]);

  const [networkProviderCount, impactProviderCount] = useMemo(() => {
    return [getNetworkProviders(providers || []).length, getImpactProviders(providers || []).length];
  }, [providers]);

  // Create GeoJSON feature collections for members, providers, and selected provider
  const memberFeatures = useMemo((): FeatureCollection<Point, MemberMapResults> | null => {
    return !!members && members.length > 0 && (showMembersWithAccess || showMembersWithoutAccess)
      ? featureCollection(
        members.map(member => point([member.lon, member.lat], member))
      )
      : featureCollection([]);
  }, [members, showMembersWithAccess, showMembersWithoutAccess]);

  const providerFeatures = useMemo((): FeatureCollection<Point, ProviderMapResults> | null => {
    return !!visibleMapProviders && visibleMapProviders.length > 0
      ? featureCollection(
        visibleMapProviders.map(provider => point([provider.longitude, provider.latitude], provider))
      )
      : featureCollection([]);
  }, [visibleMapProviders]);

  // two provider collections network / impact providers
  const selectedProviderFeature = useMemo(() => featureCollection(
    !!selectedProvider?.providerId
      ? [point([selectedProvider.longitude, selectedProvider.latitude], selectedProvider)]
      : [] // If no provider is selected, return an empty feature collection
  ), [selectedProvider]);

  useEffect(() => {
    hasRequestedMembers.current = false;
    // Drops any member response still in flight for the previous selection or refresh.
    latestMemberRequestId.current++;

    if (!filterContext.isFilterActive) {
      setLoadingCountyBorders(false);
      setLoadingMembers(false);
      setLoadingProviders(false);
      setLoadingZipCodes(false);
      setCurrentAdequacySummarySpecialtyGroup(null);
      setMembers(null);
      setCountyBorderData(null);
      latestCountyContextRequestId.current++;
      countyContextSelection.current = null;
      setCountyContextData(null);
      setSelectedNetworkProviderSortOption(ProvidersSortOptions.ServesHighToLow);
      setSelectedImpactProviderSortOption(ProvidersSortOptions.ImpactsHighToLow);
      setDataBoundingBox(null);
    } else {
      setLoadingCountyBorders(true);
      setLoadingMembers(true);
      setLoadingProviders(true);

      const getProviders = async (providerNetworkType: ProviderNetworkTypeEnum): Promise<ProviderFetchMessageData> => {
        const result =  featureFlags.enabledFeatureFlags.has(Features.SERVICE_AREA_WIDE_MAPPING_COUNTY) ?
          await projectService.servicingAccessPointsMultiRegionAsync(
            projectContext.project.projectId,
            filterContext.selectedServiceAreas,
            projectContext.runConfiguration.projectRunConfigurationId,
            filterContext.selectedSpecialtyGroupCode.key,
            providerNetworkType,
            filterContext.getCurrentServiceAreaStatus() == RegionSetStatusType.Opportunity
              ? NetworkAssessmentAnalysisType.Opportunity
              : NetworkAssessmentAnalysisType.ServiceArea,
            GainsPercentEnum.All
          )
          : await projectService.servicingAccessPoints(
            projectContext.project.projectId,
            filterContext.selectedServiceArea,
            projectContext.runConfiguration.projectRunConfigurationId,
            filterContext.selectedSpecialtyGroupCode.key,
            providerNetworkType,
            filterContext.getCurrentServiceAreaStatus() == RegionSetStatusType.Opportunity
              ? NetworkAssessmentAnalysisType.Opportunity
              : NetworkAssessmentAnalysisType.ServiceArea,
            GainsPercentEnum.All
          );

        if (!result || !result.accessPoints) {
          return Promise.reject({ providerFetchMessageStatus: ProviderFetchMessageStatus.Error, providerNetworkType });
        }

        if (result.accessPoints.length === 0) {
          return Promise.reject({ providerFetchMessageStatus: ProviderFetchMessageStatus.Unavailable, providerNetworkType });
        }

        setSummaryMap(prev => ({
          ...prev,
          [providerNetworkType]: {
            hasExcludes: result.hasExcludes,
            hasTargets: result.hasTargets,
            maxLatitude: result.maxLatitude,
            maxLongitude: result.maxLongitude,
            minLatitude: result.minLatitude,
            minLongitude: result.minLongitude,
            totalCount: result.totalCount,
          }
        }));

        setProviders((existing) => [
          ...(existing || []),
          ...result.accessPoints.map((p) => ({
            ...p,
            providerUniqueId: getProviderUniqueId(p)
          })) as ProviderMapResults[]
        ]);

        return Promise.resolve({ providerFetchMessageStatus: ProviderFetchMessageStatus.Available, providerNetworkType });
      };

      // Clear the providers array before fetching new data
      setProviders([]);
      setSummaryMap({} as ProviderResultsSummaryMap);

      // Ensure that ALL promises are resolved before setting loadingProviders to false
      Promise.allSettled(
        filterContext.selectedProviderSources.map((source) =>
          getProviders(source)
        )
      ).then(results => {
        const messages: ProviderFetchMessageData[] = [];
        results.forEach(result => {
          if (result.status === "rejected" && result.reason) {
            messages.push(result.reason);
          }
        });

        setProviderFetchMessages(messages);
      }).finally(() => {
        setLoadingProviders(false);
      });

      getRegionDetails();

      if (hasSelectedServiceArea) {
        setLoadingZipCodes(serviceAreaWideMapping);
        getZipCodeData();
      } else {
        setZipCodeData(featureCollection([]));
      }

      // Multi-county members are fetched once a member layer is turned on.
      if (hasSelectedServiceArea && filterContext.selectedSpecialtyGroupCode.key && !isMultiCounty) {
        fetchMemberData();
      } else {
        setMembers(null);
        setLoadingMembers(false);
      }

      // Outlines only change with the county selection, so a data refresh for the same selection reuses them.
      const selection = filterContext.selectedServiceAreas.join(",");
      if (serviceAreaWideMapping && (!countyContextData || countyContextSelection.current !== selection)) {
        countyContextSelection.current = selection;
        setCountyContextData(null);
        const countyContextRequestId = ++latestCountyContextRequestId.current;
        geographyService.getCountyContextByRegionSetIds(filterContext.selectedServiceAreas)
          .then((data: CountyContextModel) => {
            if (countyContextRequestId === latestCountyContextRequestId.current) setCountyContextData(data);
          })
          .catch(() => {
            if (countyContextRequestId === latestCountyContextRequestId.current) setCountyContextData(null);
          });
      }

      if(serviceAreaWideMapping)
        geographyService.getCountyBordersByRegionSetIds(filterContext.selectedServiceAreas)
          .then((countyBorderData: GeoJSON.FeatureCollection) => {
            setCountyBorderData(countyBorderData ?? { features: [], type: "FeatureCollection" })
            setLoadingCountyBorders(false);
          })
          .catch(() => {
            setCountyBorderData(null);
            setLoadingCountyBorders(false);
          });
      else
        geographyService.getCountyBordersByRegionSetId(filterContext.selectedServiceArea)
          .then((countyBorderData: GeoJSON.FeatureCollection) => {
            setCountyBorderData(countyBorderData ?? { features: [], type: "FeatureCollection" })
            setLoadingCountyBorders(false);
          })
          .catch(() => {
            setCountyBorderData(null);
            setLoadingCountyBorders(false);
          });
    }
  }, [filterContext.isFilterActive, dataRefreshCounter, filterContext.selectedServiceAreas]);

  useEffect(() => {
    if (isMultiCounty &&
      filterContext.isFilterActive &&
      (showMembersWithAccess || showMembersWithoutAccess) &&
      !hasRequestedMembers.current) {
      hasRequestedMembers.current = true;
      setLoadingMembers(true);
      fetchMemberData();
    }
  }, [isMultiCounty, filterContext.isFilterActive, filterContext.selectedServiceAreas, showMembersWithAccess, showMembersWithoutAccess, dataRefreshCounter]);

  useEffect(() => {
    // Multi-county members load on demand, so loading them must not reset the loaded state and refit the map.
    const membersLoaded = isMultiCounty || !loadingMembers;
    setDataLoaded(!loadingCountyBorders && membersLoaded && !loadingProviders && !loadingZipCodes && !!countyBorderData && !!zipCodeData);
  }, [isMultiCounty, loadingCountyBorders, loadingMembers, loadingProviders, loadingZipCodes, countyBorderData, zipCodeData]);

  useEffect(() => {
    if (dataLoaded) {

      // Frames the providers actually plotted, so ones hidden by the provider filters don't widen the view.
      const providerPoints = serviceAreaWideMapping
        ? filterMapProviders(providers || []).map(provider => point([provider.longitude, provider.latitude]))
        : Object.keys(summaryMap).map(key =>
          [
            point([summaryMap[key as ProviderNetworkTypeEnum].minLongitude, summaryMap[key as ProviderNetworkTypeEnum].minLatitude]),
            point([summaryMap[key as ProviderNetworkTypeEnum].maxLongitude, summaryMap[key as ProviderNetworkTypeEnum].maxLatitude])
          ]
        ).flat();

      const combinedFeatureCollection = featureCollection<Geometry>(providerPoints);
      combinedFeatureCollection.features.push(...countyBorderData.features);
      combinedFeatureCollection.features.push(...zipCodeData.features);

      if (combinedFeatureCollection.features.length > 0) {
        const [
          minLongitude,
          minLatitude,
          maxLongitude,
          maxLatitude
        ] = bbox(combinedFeatureCollection);

        // Fit map to bounding box
        setDataBoundingBox(new LngLatBounds(
          [minLongitude, minLatitude], [maxLongitude, maxLatitude]
        ));
      }
    }
  }, [dataLoaded]);

  const filterMapProviders = (providersToFilter: ProviderMapResults[]): ProviderMapResults[] =>
    filterProvidersByAdvancedFilters(
      filterProvidersByExclusion(
        filterProvidersByNetworkAndActionType(
          filterProvidersByNameAndNpi(providerSearchText, providersToFilter))));

  useEffect(() => {
    let updateProviders = filterMapProviders(providers || []);

    setVisibleMapProviders(updateProviders);

    updateProviders = updateProviders?.filter(p => (mapRef.current?.getBounds()).contains([p.longitude, p.latitude])) || [];
    updateProviders = sortProviders(getNetworkProviders(updateProviders || []), selectedNetworkProviderSortOption)
      .concat(sortProviders(getImpactProviders(updateProviders || []), selectedImpactProviderSortOption));

    setVisibleAccordionProviders(updateProviders);

    // Unselect a provider (its card and its icon) if after searching or filtering, it is not included in the result
    if (!!(selectedProvider as ProviderMapResults) && !updateProviders.some(p => p.providerUniqueId === selectedProvider?.providerUniqueId)) {
      setSelectedProvider(null);
    }
  }, [
    currentMapBoundary,
    providers,
    providerSearchText,
    selectedProviderFilterOptions,
    selectedNetworkProviderSortOption,
    selectedImpactProviderSortOption,
    filterGainsMinPercent,
    filterGainsMaxPercent,
    filterServesMinPercent,
    filterServesMaxPercent,
    filterImpactsMinPercent,
    filterImpactsMaxPercent,
    dataRefreshCounter,
    showExcludedProviders,
    selectedLineOfBusiness,
    selectedVolumeIndicators,
    selectedEffectiveness,
    selectedEfficiency
  ]);

  const getSelectedSortValue = () => {
    return providerAccordionSelectedTab === ProviderAccordionSelectedTab.NetworkProviders
      ? selectedNetworkProviderSortOption
      : selectedImpactProviderSortOption;
  };

  useEffect(() => {
    if (filterContext.selectedProviderSources.includes(ProviderNetworkTypeEnum.Network)) {
      setShowNetworkProvidersTab(true);
      setProviderAccordionSelectedTab(ProviderAccordionSelectedTab.NetworkProviders);
    }
    else {
      setShowNetworkProvidersTab(false);
    }

    if (filterContext.selectedProviderSources.includes(ProviderNetworkTypeEnum.Market) ||
      filterContext.selectedProviderSources.includes(ProviderNetworkTypeEnum.Prospect)) {
      setShowOutOfNetworkProvidersTab(true);

      if (!filterContext.selectedProviderSources.includes(ProviderNetworkTypeEnum.Network)) {
        setProviderAccordionSelectedTab(ProviderAccordionSelectedTab.OutOfNetworkProviders);
      }
    }
    else {
      setShowOutOfNetworkProvidersTab(false);
    }

    setHideNetworkProviderFilter(!filterContext.selectedProviderSources.includes(ProviderNetworkTypeEnum.Network));
    setHideProspectProviderFilter(!filterContext.selectedProviderSources.includes(ProviderNetworkTypeEnum.Prospect));
    setHideMarketProviderFilter(!filterContext.selectedProviderSources.includes(ProviderNetworkTypeEnum.Market));
  }, [filterContext.selectedProviderSources]);

  useEffect(() => {
    const networkProviders = getNetworkProviders(providers || []);
    const impactProviders = getImpactProviders(providers || []);

    const availableFilterOptions: ProvidersFilterOptionsType[] = [];
      if (impactProviders.some(p => p.providerNetworkType === ProviderNetworkTypeEnum.Market &&
        p.scenarioModelProviderStatus !== ProviderActionStatusEnum.FullyTargeted)) {
        availableFilterOptions.push(availableMarketFilterOption);
      }

      if (impactProviders.some(p => p.providerNetworkType === ProviderNetworkTypeEnum.Prospect &&
        p.scenarioModelProviderStatus !== ProviderActionStatusEnum.FullyTargeted)) {
        availableFilterOptions.push(availableProspectFilterOption);
      }

      if (networkProviders.some(p => p.scenarioModelProviderStatus !== ProviderActionStatusEnum.FullyTerminated) ||
        (networkProviders.length > 0 && (!userContext.roles.includes(Roles.TERM_PROVIDER_ADMIN) && !userContext.roles.includes(Roles.TERM_PROVIDER_VIEWER)))) {
        availableFilterOptions.push(nonTerminatedFilterOption);
      }

    const targetFilterOptions: ProvidersFilterOptionsType[] = [];
    const excludedFilterOptions: ProvidersFilterOptionsType[] = [];
    const terminatedFilterOptions: ProvidersFilterOptionsType[] = [];
    if (!!impactProviders && !!projectContext.runConfiguration?.isLatestPublished) {
      if (impactProviders.some(p => p.providerNetworkType === ProviderNetworkTypeEnum.Market &&
        p.scenarioModelProviderStatus === ProviderActionStatusEnum.FullyTargeted)) {
          targetFilterOptions.push(targetedMarketFilterOption);
      }

      if (impactProviders.some(p => p.providerNetworkType === ProviderNetworkTypeEnum.Prospect &&
        p.scenarioModelProviderStatus === ProviderActionStatusEnum.FullyTargeted)) {
          targetFilterOptions.push(targetedProspectFilterOption);
      }

      if (impactProviders.some(p => p.providerNetworkType === ProviderNetworkTypeEnum.Market &&
        p.scenarioModelProviderStatus === ProviderActionStatusEnum.FullyExcluded)) {
          excludedFilterOptions.push(excludedMarketFilterOption);
      }

      if (impactProviders.some(p => p.providerNetworkType === ProviderNetworkTypeEnum.Prospect &&
        p.scenarioModelProviderStatus === ProviderActionStatusEnum.FullyExcluded)) {
          excludedFilterOptions.push(excludedProspectFilterOption);
      }

        if (networkProviders.some(p =>
          p.scenarioModelProviderStatus === ProviderActionStatusEnum.FullyTerminated) &&
          (userContext.roles.includes(Roles.TERM_PROVIDER_ADMIN) || userContext.roles.includes(Roles.TERM_PROVIDER_VIEWER))) {
          terminatedFilterOptions.push(terminatedFilterOption);
      }
    }

    let updatedVisibleProviderFilterOptions: ProvidersFilterOptionsType[] = [];

    if (
      excludedFilterOptions.length > 0 ||
      targetFilterOptions.length > 0 ||
      terminatedFilterOptions.length > 0 ||
      availableFilterOptions.length > 0
    ) {
        updatedVisibleProviderFilterOptions = baseProvidersFilterOptionsAdvancedFiltersTemplate.map(option => ({
          ...option,
          children: option.children ? [...option.children] : [],
        }));

        availableFilterOptions.forEach(p => {
          if (p.type === ProvidersFilterOptions.NonTerminated) {
            updatedVisibleProviderFilterOptions.find(option => option.type === ProvidersFilterOptions.Network)?.children.push(p);
          }

          if (p.type === ProvidersFilterOptions.AvailableMarket) {
            updatedVisibleProviderFilterOptions.find(option => option.type === ProvidersFilterOptions.Market)?.children.push(p);
          }

          if (p.type === ProvidersFilterOptions.AvailableProspect) {
            updatedVisibleProviderFilterOptions.find(option => option.type === ProvidersFilterOptions.Prospect)?.children.push(p);
          }
        });

        targetFilterOptions.forEach(p => {
          if (p.type === ProvidersFilterOptions.TargetedMarket) {
            updatedVisibleProviderFilterOptions.find(option => option.type === ProvidersFilterOptions.Market)
              ?.children.push(p);
          }

          if (p.type === ProvidersFilterOptions.TargetedProspect) {
            updatedVisibleProviderFilterOptions.find(option => option.type === ProvidersFilterOptions.Prospect)
              ?.children.push(p);
          }
        });

        excludedFilterOptions.forEach(p => {
            if (p.type === ProvidersFilterOptions.ExcludedMarket) {
              updatedVisibleProviderFilterOptions.find(option => option.type === ProvidersFilterOptions.Market)
                ?.children.push(p);
            }

            if (p.type === ProvidersFilterOptions.ExcludedProspect) {
              updatedVisibleProviderFilterOptions.find(option => option.type === ProvidersFilterOptions.Prospect)
                ?.children.push(p);
            }
        });

        terminatedFilterOptions.forEach(p => {
          if (p.type === ProvidersFilterOptions.Terminated) {
            updatedVisibleProviderFilterOptions.find(option => option.type === ProvidersFilterOptions.Network)
              .children.push(p);
          }
        });

      setProviderFilterOptions(updatedVisibleProviderFilterOptions);

      const visibleTargetChildrenOptions = updatedVisibleProviderFilterOptions
        .filter(option => option.type !== ProvidersFilterOptions.Excluded)
        .flatMap(option =>
          option.children ? option.children.map(child => child.type) : [option.type]
        );

      // Check which options have not been selected nor previously displayed
      const newOptions = visibleTargetChildrenOptions.filter(option =>
        !selectedProviderFilterOptions.includes(option) &&
        !previouslyVisibleProviderFilterOptions.current.has(option) &&
        !baseProvidersFilterOptionsSet.has(option)
      );

         if (selectedProviderFilterOptions.length > 0 && newOptions.length > 0) {
          setSelectedProviderFilterOptions(prev => [...prev, ...newOptions]);
        }

      // Update the ref for the next render/update
      previouslyVisibleProviderFilterOptions.current = new Set(visibleTargetChildrenOptions);

    } else {
      // If no targeted data is present, use the initial filters
      setProviderFilterOptions(baseProvidersFilterOptions);
    }
  }, [providers, projectContext.runConfiguration?.isLatestPublished]);

  useEffect(() => {
    setSelectedVolumeIndicators(filterContext.selectedVolumeIndicators);
  }, [filterContext.selectedVolumeIndicators]);

  useEffect(() => {
    if (!selectedLineOfBusiness) {
      setSelectedVolumeIndicators([]);
    }
  }, [selectedLineOfBusiness])

  const filterProvidersByNameAndNpi = <T extends { displayName: string; npi: string }>(searchText: string, providers: T[]): T[] => {
    if (!searchText)
      return providers;

    return providers.filter(provider =>
      (provider.displayName?.toLowerCase().includes(searchText.toLocaleLowerCase()) ?? false) ||
      (provider.npi?.toLowerCase().includes(searchText.toLowerCase()) ?? false))
  };

  const filterProvidersByNetworkAndActionType = (providers: ProviderMapResults[]): ProviderMapResults[] => {
    // If there are no providers, return an empty array
    if (!providers || providers.length === 0) {
      return [];
    }

    if (selectedProviderFilterOptions.length === 0) {
      return providers;
    }

    return providers.filter(provider => {
      if (selectedProviderFilterOptions.includes(ProvidersFilterOptions.TargetedMarket) &&
        provider.scenarioModelProviderStatus === ProviderActionStatusEnum.FullyTargeted &&
        provider.providerNetworkType === ProviderNetworkTypeEnum.Market) {
        return true;
      }

      if (selectedProviderFilterOptions.includes(ProvidersFilterOptions.TargetedProspect) &&
        provider.scenarioModelProviderStatus === ProviderActionStatusEnum.FullyTargeted &&
        provider.providerNetworkType === ProviderNetworkTypeEnum.Prospect) {
        return true;
      }

      if (selectedProviderFilterOptions.includes(ProvidersFilterOptions.ExcludedMarket) &&
        provider.scenarioModelProviderStatus === ProviderActionStatusEnum.FullyExcluded &&
        provider.providerNetworkType === ProviderNetworkTypeEnum.Market) {
        return true;
      }

      if (selectedProviderFilterOptions.includes(ProvidersFilterOptions.ExcludedProspect) &&
        provider.scenarioModelProviderStatus === ProviderActionStatusEnum.FullyExcluded &&
        provider.providerNetworkType === ProviderNetworkTypeEnum.Prospect) {
        return true;
      }

      if (selectedProviderFilterOptions.includes(ProvidersFilterOptions.NonTerminated) &&
        provider.scenarioModelProviderStatus !== ProviderActionStatusEnum.FullyTerminated &&
        provider.providerNetworkType === ProviderNetworkTypeEnum.Network) {
        return true;
      }

      if (selectedProviderFilterOptions.includes(ProvidersFilterOptions.Terminated) &&
        provider.scenarioModelProviderStatus === ProviderActionStatusEnum.FullyTerminated &&
        provider.providerNetworkType === ProviderNetworkTypeEnum.Network) {
        return true;
      }

      if (selectedProviderFilterOptions.includes(ProvidersFilterOptions.AvailableProspect) &&
        provider.providerNetworkType === ProviderNetworkTypeEnum.Prospect &&
        provider.scenarioModelProviderStatus !== ProviderActionStatusEnum.FullyTargeted &&
        provider.scenarioModelProviderStatus !== ProviderActionStatusEnum.FullyExcluded) {
        return true;
      }

      if (selectedProviderFilterOptions.includes(ProvidersFilterOptions.AvailableMarket) &&
        provider.providerNetworkType === ProviderNetworkTypeEnum.Market &&
        provider.scenarioModelProviderStatus !== ProviderActionStatusEnum.FullyTargeted &&
        provider.scenarioModelProviderStatus !== ProviderActionStatusEnum.FullyExcluded) {
        return true;
      }

      return false;
    });
  }

  const filterProvidersByAdvancedFilters = (providers: ProviderMapResults[]): ProviderMapResults[] => {
    if (!providers || providers.length === 0) {
      return providers;
    }

    const filteredProviders = providers
      .filter(provider => {
        if (provider.servesPercent < filterServesMinPercent) {
          return false;
        }
        if (provider.servesPercent > filterServesMaxPercent) {
          return false;
        }

        if (pciEnabled) {

          if (selectedLineOfBusiness && selectedVolumeIndicators.length && selectedVolumeIndicators.length != filterContext.mappedVolumeIndicators.length) {
            const volumeIndicatorValues = selectedVolumeIndicators.map(x => x.key as ClaimsActivityVolumeLabelEnum);

            if (selectedLineOfBusiness == ClaimsActivityLineOfBusinessTypeEnum.Total) {
              if (!claimsFiltersIncludesProvider(provider.totalClaimsVolume, volumeIndicatorValues)) {
                return false;
              };
            }
            else if (selectedLineOfBusiness == ClaimsActivityLineOfBusinessTypeEnum.Commercial) {
              if (!claimsFiltersIncludesProvider(provider.commercialClaimsVolume, volumeIndicatorValues)) {
                return false;
              };
            }
            else if (selectedLineOfBusiness == ClaimsActivityLineOfBusinessTypeEnum.Medicare) {
              if (!claimsFiltersIncludesProvider(provider.medicareClaimsVolume, volumeIndicatorValues)) {
                return false;
              };
            }
            else if (selectedLineOfBusiness == ClaimsActivityLineOfBusinessTypeEnum.Medicaid) {
              if (!claimsFiltersIncludesProvider(provider.medicaidClaimsVolume, volumeIndicatorValues)) {
                return false;
              };
            }
            else if (selectedLineOfBusiness == ClaimsActivityLineOfBusinessTypeEnum.VA) {
              if (!claimsFiltersIncludesProvider(provider.vaClaimsVolume, volumeIndicatorValues)) {
                return false;
              };
            }
            else if (selectedLineOfBusiness == ClaimsActivityLineOfBusinessTypeEnum.TRICARE) {
              if (!claimsFiltersIncludesProvider(provider.tricareClaimsVolume, volumeIndicatorValues)) {
                return false;
              };
            }
          }
        }

        if (selectedEffectiveness.length && !selectedEffectiveness.includes(provider.effectivenessRank ?? null)) {
          return false;
        }

        if (selectedEfficiency.length && !selectedEfficiency.includes(provider.efficiencyRank ?? null)) {
          return false;
        }

        if(provider.providerNetworkType === ProviderNetworkTypeEnum.Network) {
          return true;
        }

        if (provider.gainsPercent < filterGainsMinPercent) {
          return false;
        }
        if (provider.gainsPercent > filterGainsMaxPercent) {
          return false;
        }

        if (provider.impactsPercent < filterImpactsMinPercent) {
          return false;
        }
        if (provider.impactsPercent > filterImpactsMaxPercent) {
          return false;
        }

        return true;
      });

    return filteredProviders;
  }

  const filterProvidersByExclusion = (providers: ProviderMapResults[]): ProviderMapResults[] => {
    if (showExcludedProviders) {
      return providers;
    }

    return providers.filter(provider => provider.scenarioModelProviderStatus !== ProviderActionStatusEnum.FullyExcluded);
  }

  const setSelectedSortValue = (selectSort: ProvidersSortOptions) => {
    if (providerAccordionSelectedTab == ProviderAccordionSelectedTab.NetworkProviders)
      setSelectedNetworkProviderSortOption(selectSort);
    else
      setSelectedImpactProviderSortOption(selectSort);
  }

  const resetMapData = () => {
    setSelectedProviderFilterOptions([]);
    previouslyVisibleProviderFilterOptions.current = new Set();
    setSelectedNetworkProviderSortOption(ProvidersSortOptions.ServesHighToLow);
    setSelectedImpactProviderSortOption(ProvidersSortOptions.ImpactsHighToLow);
    setProviderSearchText("");
    setSelectedProvider(null);
    setMembers(null);
    setProviders([]);
    setShowMembersWithAccess(true);
    setShowMembersWithoutAccess(true);
    setMapStyle(mappingContext.qesOutdoorsStyle);
    setDataRefreshCounter(0);
    setShowZipCodes(false);
    setShowZipPoints(false);
    setZipCodeData(featureCollection([]));
  };

  const applyMemberLayerDefaults = (): void => {
    setShowMembersWithAccess(!isMultiCounty);
    setShowMembersWithoutAccess(!isMultiCounty);
  };

  const triggerDataRefresh = (): void => {
    handleProviderPopupClose();
    setProviders([]);
    setDataRefreshCounter(prev => prev + 1);
  };

  const sortProviders = (
    <T extends ProviderMapResults>(
      providers: T[],
      sortOption: ProvidersSortOptions
    ): T[] => {
      const sorted = [...providers];
      switch (sortOption) {
        case ProvidersSortOptions.Alphabetical:
          return sorted.sort((a, b) => a.displayName.localeCompare(b.displayName));
        case ProvidersSortOptions.ReverseAlphabetical:
          return sorted.sort((a, b) => b.displayName.localeCompare(a.displayName));
        case ProvidersSortOptions.ServesHighToLow:
          return sorted.sort((a, b) => (b as ProviderMapResults).servesPercent - (a as ProviderMapResults).servesPercent);
        case ProvidersSortOptions.ServesLowToHigh:
          return sorted.sort((a, b) => (a as ProviderMapResults).servesPercent - (b as ProviderMapResults).servesPercent);
        case ProvidersSortOptions.GainsHighToLow:
          return sorted.sort((a, b) => (b as ProviderMapResults).gainsPercent - (a as ProviderMapResults).gainsPercent);
        case ProvidersSortOptions.GainsLowToHigh:
          return sorted.sort((a, b) => (a as ProviderMapResults).gainsPercent - (b as ProviderMapResults).gainsPercent);
        case ProvidersSortOptions.ImpactsHighToLow:
          return sorted.sort((a, b) => (b as ProviderMapResults).impactsPercent - (a as ProviderMapResults).impactsPercent);
        case ProvidersSortOptions.ImpactsLowToHigh:
          return sorted.sort((a, b) => (a as ProviderMapResults).impactsPercent - (b as ProviderMapResults).impactsPercent);
        default:
          return sorted;
      }
    }
  );

  const getProviderUniqueId = (provider: ProviderMapResults) => {
    return `${provider.providerNetworkType}-${provider.providerId}-${provider.specialtyCode}`;
  };

  const handleProviderCardClick = (provider: ProviderMapResults) => {
    setSelectedProvider(provider);
    setIsProviderPopupOpen(true);
  };

  const handleProviderIconClick = (e: ReactMapGL.MapMouseEvent) => {
    const featureProviders = e.features;

    if (!featureProviders || featureProviders.length === 0) {
      return;
    }

    const clickedProvider = featureProviders.find((p) =>
      PROVIDERS_LAYER_ID === p.layer.id
    );

    if (clickedProvider && clickedProvider.properties) {
      const clickedProviderId = clickedProvider.properties.providerUniqueId;
      let selectedProvider: ProviderMapResults = null;

      // Open the provider accordion if it is closed
      if (!isProviderAccordionOpen) {
        setIsProviderAccordionOpen(!isProviderAccordionOpen);
      }

      // Find the provider in the visible providers list
      selectedProvider = providers?.find(item => item.providerUniqueId === clickedProviderId);

      if (!selectedProvider) {
        // No provider is selected, stay put
      }
      else if (selectedProvider.providerNetworkType === ProviderNetworkTypeEnum.Network) {
        setProviderAccordionSelectedTab(ProviderAccordionSelectedTab.NetworkProviders);
      }
      else {
        setProviderAccordionSelectedTab(ProviderAccordionSelectedTab.OutOfNetworkProviders);
      }

      if (!!selectedProvider) {
        setSelectedProvider(selectedProvider);
        setIsProviderPopupOpen(true);
      }
    }
  };

  const handleProviderPopupClose = () => {
    setIsProviderPopupOpen(false);
    setSelectedProvider(null);
  };

  const filterAvailableProviderSources = (providerFilterOptions: ProvidersFilterOptions[]): ProvidersFilterOptions[] => {
    return providerFilterOptions.filter(option => {
      if (option === ProvidersFilterOptions.Network && hideNetworkProviderFilter) {
        return false;
      }
      if (option === ProvidersFilterOptions.Market && hideMarketProviderFilter) {
        return false;
      }
      if (option === ProvidersFilterOptions.Prospect && hideProspectProviderFilter) {
        return false;
      }

      return true;
    });
  }

  const preferredProviderSourceFilterOrder = [
    ProvidersFilterOptions.Network,
    ProvidersFilterOptions.NonTerminated,
    ProvidersFilterOptions.Terminated,
    ProvidersFilterOptions.Market,
    ProvidersFilterOptions.AvailableMarket,
    ProvidersFilterOptions.TargetedMarket,
    ProvidersFilterOptions.ExcludedMarket,
    ProvidersFilterOptions.Prospect,
    ProvidersFilterOptions.AvailableProspect,
    ProvidersFilterOptions.TargetedProspect,
    ProvidersFilterOptions.ExcludedProspect,
  ];

  const providerSourceFiltersComparer = (a: ProvidersFilterOptions, b: ProvidersFilterOptions) => {
    const firstIndex = preferredProviderSourceFilterOrder.indexOf(a);
    const secondIndex = preferredProviderSourceFilterOrder.indexOf(b);

    if (firstIndex === -1 || secondIndex === -1) {
      if (firstIndex === -1 && secondIndex !== -1) return 1;
      if (firstIndex !== -1 && secondIndex === -1) return -1;
      return 0;
    }

    return firstIndex - secondIndex;
  }

  const claimsFiltersIncludesProvider = (indicatorValue: ClaimsActivityVolumeLabelEnum, selectedIndicators: ClaimsActivityVolumeLabelEnum[]) => {
    if (pcaIndicatorIncludesInsufficient(indicatorValue)) {
      indicatorValue = ClaimsActivityVolumeLabelEnum.InsufficientData;
    }

    if (selectedIndicators.includes(indicatorValue ?? ClaimsActivityVolumeLabelEnum.InsufficientData)) {
      return true;
    }

    return false;
  }

  const pcaIndicatorIncludesInsufficient = (value: string) => {
    return Object.values(InsufficientClaimsVolumeLabels).includes(value as InsufficientClaimsVolumeLabels);
  }

  /* oxlint-disable sort-keys */
  const contextValue: NetworkCoverageMapDataContextProps = {
    applyMemberLayerDefaults,
    availablePageSizes,
    countyBorderData,
    countyContextData,
    currentAdequacySummarySpecialtyGroup,
    currentMapBoundary,
    dataBoundingBox,
    dataLoaded,
    filterGainsMaxPercent,
    filterGainsMinPercent,
    filterImpactsMaxPercent,
    filterImpactsMinPercent,
    filterServesMaxPercent,
    filterServesMinPercent,
    filterProvidersByNetworkAndActionType,
    filterAvailableProviderSources,
    getSelectedSortValue,
    handleProviderCardClick,
    handleProviderIconClick,
    handleProviderPopupClose,
    hideNetworkProviderFilter,
    hideMarketProviderFilter,
    hideProspectProviderFilter,
    impactCardHeight,
    impactPageSize,
    providersFilterOptions,
    impactProviderCount,
    impactProvidersPage,
    isProviderAccordionOpen,
    isProviderPopupOpen,
    loadingMembers,
    loadingProviders,
    mapRef,
    mapStyle,
    memberFeatures,
    members,
    minZoomLevel,
    networkCardHeight,
    networkPageSize,
    networkProviderCount,
    networkProvidersPage,
    pagedImpactProviders,
    pagedNetworkProviders,
    pcaIndicatorIncludesInsufficient,
    providerAccordionSelectedTab,
    providerFeatures,
    providerFetchMessages,
    providers,
    providerSearchText,
    providerSourceFiltersComparer,
    resetMapData,
    selectedEffectiveness,
    selectedEfficiency,
    selectedLineOfBusiness,
    selectedProvider,
    selectedProviderFeature,
    selectedProviderFilterOptions,
    selectedVolumeIndicators,
    totalImpactPages,
    totalNetworkPages,
    visibleAccordionProviders,
    setCurrentMapBoundary,
    setFilterGainsMaxPercent,
    setFilterGainsMinPercent,
    setFilterImpactsMaxPercent,
    setFilterImpactsMinPercent,
    setFilterServesMaxPercent,
    setFilterServesMinPercent,
    setImpactPageSize,
    setImpactProvidersPage,
    setIsProviderAccordionOpen,
    setMapStyle,
    setMinZoomLevel,
    setNetworkPageSize,
    setPagedImpactProviders,
    setPagedNetworkProviders,
    setProviderAccordionSelectedTab,
    setProviderSearchText,
    setNetworkProvidersPage,
    setSelectedEffectiveness,
    setSelectedEfficiency,
    setSelectedLineOfBusiness,
    setSelectedProviderFilterOptions,
    setSelectedSortValue,
    setSelectedVolumeIndicators,
    setShowMembersWithAccess,
    setShowMembersWithoutAccess,
    setTotalImpactPages,
    setTotalNetworkPages,
    setShowZipCodes,
    setShowZipPoints,
    setShowExcludedProviders,
    showMembersWithAccess,
    showMembersWithoutAccess,
    showNetworkProvidersTab,
    showOutOfNetworkProvidersTab,
    showZipCodes,
    showZipPoints,
    showExcludedProviders,
    toggleProviderAccordion,
    triggerDataRefresh,
    zipCodeData
  };
  /* oxlint-enable sort-keys */

  return (
    <NetworkCoverageMapDataContext value={contextValue}>
      {props.children}
    </NetworkCoverageMapDataContext>
  )
}

export { NetworkCoverageMapDataContext, NetworkCoverageMapDataContextProvider, NetworkCoverageMapDataContextProps };